import { useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';
import { Box } from '@mui/material';
import { common } from '@mui/material/colors';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseSelect, ListPage, ListRow } from '/src/components';
import { useFilterStore, usePaymentStore, useUserStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Note } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';
import * as utils from '/src/utils';

/**
 * List Payments Page
 */
export const ListPaymentsPage = observer(() => {
  const defaultSortType = 'datePaid';
  const defaultSortReverse = true;

  /** Context **/
  const paymentStore = usePaymentStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();

  /** State **/
  const [search, setSearch] = useState<Record<string, any>>({});
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState<boolean>(defaultSortReverse);
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/
  const payments = paymentStore.payments;
  const isFiltered = !!Object.values(search).length;

  const companies = useMemo(() => filterStore.companyListGet(['name', '_id', 'isLender']), [filterStore._companyList]);

  const listData = useMemo(() => {
    if (payments) {
      let list = payments.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      if (search.company) list = list.filter((payment) => payment.company?._id === search.company);

      return list;
    }
  }, [payments, search, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /** Effects **/

  useEffect(() => {
    paymentStore.query(search);
  }, []);

  useEffect(() => {
    if (userStore.user && !userStore.isAdmin) {
      setSearch({ ...search, company: userStore.user.company });
    }
  }, [userStore.user, userStore.isAdmin]);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Payment}
      headerItems={[
        { flex: 5, propName: 'notes', label: 'Notes' },
        { flex: 1, propName: 'name', label: 'Payment' },
        { flex: 1, propName: 'company.name', label: 'Company' },
        { flex: 1, propName: 'asset.name', label: 'Asset' },
        { flex: 1, propName: 'program', label: 'Program' },
        { flex: 1, propName: 'amount', label: 'Amount' },
        { flex: 1, propName: 'paymentType', label: 'Payment Type' },
        { flex: 1, propName: 'datePaid', label: 'Date Paid' },
        { flex: 1, propName: 'dateDeposited', label: 'Date Deposited' },
      ]}
      toolbarItems={
        <>
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  value={search.company}
                  updateValue={(id) => setSearch({ ...search, company: id })}
                  options={companies
                    .filter((company) => !company.isLender)
                    .map((company) => ({ label: company.name, value: company._id }))}
                  placeholder="All Companies"
                  enablePlaceholderOption
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
        </>
      }
      isFiltered={isFiltered}
      noFilteredDataMessage="No Payments found for that Company."
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      notes={notes}
      notesName={notesName}
      setNotes={setNotes}
      setNotesName={setNotesName}
    >
      {listData?.map((payment, i) => (
        <ListRow
          key={payment._id}
          items={[
            {
              propName: 'notes',
              isNotes: true,
              flex: 5,
              hide: !payment.notes?.length,
              showNotesPopup: () => showNotesPopup(payment.notes, payment.name),
            },
            { propName: 'name', content: payment.name, url: `/payments/${payment._id}` },
            { propName: 'company.name', content: payment.company.name, url: `/companies/${payment.company._id}` },
            { propName: 'asset.name', content: payment.asset?.name, url: `/assets/${payment.asset?._id}` },
            { propName: 'program', content: payment.program },
            { propName: 'amount', content: utils.currency.format(payment.amount), className: 'text-right' },
            { propName: 'paymentType', content: payment.paymentType },
            { propName: 'datePaid', content: dayjs(payment.datePaid).format(mediumDate) },
            { propName: 'dateDeposited', content: dayjs(payment.dateDeposited).format(mediumDate) },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
